import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Minus } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, SectionHeader } from "../components/site/Reveal";
import { CERAMIC_PACKAGES, CERAMIC_COMPARISON, VEHICLE_SIZES } from "../lib/content";

const ComparisonCell = ({ value }) => {
  if (value === "yes") return <Check size={16} className="text-silver mx-auto" />;
  if (value === "no") return <Minus size={16} className="text-gray-600 mx-auto" />;
  return <span className="text-gray-300 font-light text-xs sm:text-sm">{value}</span>;
};

export default function CeramicCoating() {
  const [openId, setOpenId] = useState(null);

  return (
    <>
      <Seo title="Ceramic Coating Melbourne" description="Professional ceramic coating in Noble Park, Melbourne. SPECA ONE (1 Year), SPECA PRO (3 Year) and SPECA PREMIUM (5+ Year) packages with full inclusions and transparent vehicle-size pricing." path="/ceramic-coating" />
      <section className="pt-32 pb-16 md:pt-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader overline="Ceramic Coating" title="Long-term protection, maximum gloss." description="Three ceramic coating tiers with transparent vehicle-size pricing. Every package includes thorough preparation and professional application — expand any package to see exactly what's included." />
        </div>
      </section>

      {/* Package cards */}
      <section data-testid="ceramic-packages" className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            {CERAMIC_PACKAGES.map((p) => {
              const open = openId === p.id;
              return (
                <Reveal key={p.id}>
                  <div data-testid={`ceramic-${p.id}`} className={`p-7 flex flex-col ${p.popular ? "glass-card silver-glow" : "matte"}`}>
                    {p.popular && <span data-testid="ceramic-popular-badge" className="inline-block bg-silver text-black text-[9px] font-bold uppercase tracking-widest px-2 py-1 mb-3 self-start">Most Popular</span>}
                    <h3 className="font-display text-2xl font-bold tracking-tight text-white">{p.name}</h3>
                    <p className="overline mt-2">{p.duration}</p>
                    <p className="text-gray-400 text-sm font-light mt-1">{p.description}</p>
                    <div className="h-px bg-white/10 my-6" />
                    <div className="space-y-3">
                      {VEHICLE_SIZES.map((s) => (
                        <div key={s.id} className="flex items-center justify-between text-sm">
                          <div>
                            <p className="text-white">{s.label} Vehicle</p>
                            <p className="text-gray-500 text-xs">{s.description}</p>
                          </div>
                          <p className="font-mono-tech text-silver">${p.prices[s.id].toLocaleString()}</p>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      data-testid={`ceramic-inclusions-toggle-${p.id}`}
                      onClick={() => setOpenId(open ? null : p.id)}
                      aria-expanded={open}
                      className="mt-7 w-full flex items-center justify-between border border-white/15 px-4 py-3 text-xs font-bold uppercase tracking-widest text-white hover:border-silver transition-colors"
                    >
                      View Full Inclusions
                      <ChevronDown size={16} className={`text-silver transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>

                    {open && (
                      <div data-testid={`ceramic-inclusions-${p.id}`} className="mt-5 space-y-6 border-t border-white/10 pt-5">
                        {p.leadIn && <p className="text-silver text-xs font-light italic">{p.leadIn}</p>}
                        {p.inclusionGroups.map((g) => (
                          <div key={g.title}>
                            <p className="overline">{g.title}</p>
                            <ul className="mt-3 space-y-2.5">
                              {g.items.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-gray-300 text-sm font-light">
                                  <Check size={15} className="text-silver mt-0.5 shrink-0" />{item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-7 flex flex-col gap-3">
                      <Link to="/contact" state={{ estimate: { pkg: p.name } }} data-testid={`ceramic-book-${p.id}`} className={`w-full inline-block text-center py-3.5 uppercase tracking-widest text-xs font-bold transition-all ${p.popular ? "bg-silver hover:bg-white text-black" : "bg-white text-black hover:bg-silver"}`}>
                        Book Now
                      </Link>
                      <Link to="/contact" data-testid={`ceramic-enquire-${p.id}`} className="w-full inline-block text-center py-3.5 uppercase tracking-widest text-xs font-bold bg-transparent border border-white/20 text-white hover:border-white transition-all">
                        Enquire
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="text-gray-500 text-xs font-light mt-6 text-center italic">Durability claims are subject to manufacturer specifications, product selection and maintenance.</p>
        </div>
      </section>

      {/* Comparison table */}
      <section data-testid="ceramic-comparison" className="py-20 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <span className="overline">Compare Packages</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mt-4 mb-10">Side by side, no guesswork.</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-4 pr-4 overline">Feature</th>
                  {CERAMIC_PACKAGES.map((p) => (
                    <th key={p.id} className={`py-4 px-4 font-display text-sm sm:text-base font-bold tracking-tight ${p.popular ? "text-silver" : "text-white"}`}>
                      {p.name}
                      {p.popular && <span className="block text-[9px] font-bold uppercase tracking-widest text-silver mt-1">Most Popular</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CERAMIC_COMPARISON.map((row, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-4 pr-4 text-gray-400 text-xs sm:text-sm font-light">{row.feature}</td>
                    {row.values.map((v, j) => (
                      <td key={j} className={`py-4 px-4 text-center ${CERAMIC_PACKAGES[j].popular ? "bg-white/[0.03]" : ""}`}>
                        <ComparisonCell value={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-500 text-xs mt-6 italic">Ceramic coatings are not scratch-proof and do not eliminate the need for washing. Additional paint correction beyond the included stage is quoted separately.</p>
        </div>
      </section>
    </>
  );
}
